/**
 * EntityMessageBoard — Facebook-style discussion board that any page
 * can attach to via an (entityType, entityId) pair.
 *
 * Rich composer:
 *   • Post-type dropdown: Comment · Addition/New Post · Correction · Suggestion · Other
 *   • Free-form text (up to 3000 chars)
 *   • Attach up to 6 images (each ≤ 5 MB, jpg/png/webp/heic)
 *   • Attach one YouTube / Vimeo video URL (rendered as embed)
 *   • Attach one external link URL (rendered as a preview card)
 *
 * Guests see a "Sign in to join" nudge. Members can delete their
 * own posts; admins can delete anyone's.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Image as ImageIcon, Video, Link as LinkIcon, X,
  MessageCircle, Send, Trash2, Loader2, LogIn, UserRound,
} from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/* ─── small utilities ─────────────────────────────────────────────── */
const POST_TYPES = [
  { key: "comment",        label: "Comment",         chip: "#334155" },
  { key: "complaint",      label: "Complaint",       chip: "#B91C1C" },
  { key: "suggestion",     label: "Suggestion",      chip: "#2563EB" },
  { key: "abuse_report",   label: "Abuse report",    chip: "#991B1B" },
  { key: "business_reply", label: "Business reply",  chip: "#166534" },
  // Legacy — kept so older posts still render but hidden from composer.
  { key: "addition",       label: "Addition",        chip: "#059669", legacy: true },
  { key: "correction",     label: "Correction",      chip: "#DC2626", legacy: true },
  { key: "other",          label: "Other",           chip: "#7C3AED", legacy: true },
];
const COMPOSER_POST_TYPES = POST_TYPES.filter((p) => !p.legacy);
const REACTIONS = [
  { key: "thumbs_up", emoji: "👍", label: "Like" },
  { key: "heart",     emoji: "❤️", label: "Love" },
  { key: "pray",      emoji: "🙏", label: "Thank you" },
  { key: "laugh",     emoji: "😂", label: "Haha" },
  { key: "sad",       emoji: "😢", label: "Sad" },
  { key: "think",     emoji: "🤔", label: "Hmm" },
];
const MAX_IMAGES  = 6;
const MAX_BYTES   = 5 * 1024 * 1024;
const API_ORIGIN  = process.env.REACT_APP_BACKEND_URL || "";

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d < 30)    return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

// Some upload endpoints return an app-relative /api/... path — turn
// that into an absolute URL so <img> tags can load it.
const toAbsoluteUrl = (u) =>
  !u ? "" : /^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith("/") ? "" : "/"}${u}`;

// Extract a YouTube or Vimeo video id from a URL. Deliberately loose
// so paste-anywhere still works.
function parseVideoEmbed(u) {
  if (!u) return null;
  try {
    const url = new URL(u);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.slice(1);
      if (id) return { src: `https://www.youtube.com/embed/${id}`, kind: "youtube" };
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      const v = url.searchParams.get("v");
      if (v) return { src: `https://www.youtube.com/embed/${v}`, kind: "youtube" };
      const m = url.pathname.match(/^\/(shorts|embed|v|live)\/([A-Za-z0-9_-]+)/);
      if (m) return { src: `https://www.youtube.com/embed/${m[2]}`, kind: "youtube" };
    }
    if (host.endsWith("vimeo.com")) {
      const id = url.pathname.split("/").filter(Boolean).pop();
      if (id && /^\d+$/.test(id)) return { src: `https://player.vimeo.com/video/${id}`, kind: "vimeo" };
    }
  } catch { /* fallthrough */ }
  return null;
}

export default function EntityMessageBoard({ entityType, entityId, title = "Community feed" }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");             // "all" | one of POST_TYPES.key
  const [replyingTo, setReplyingTo] = useState(null); // root msg id we're replying to
  const [replyText,  setReplyText]  = useState("");
  const [replyBusy,  setReplyBusy]  = useState(false);
  const [menuFor, setMenuFor] = useState(null);       // id of the message showing kebab menu

  /* ─── composer state ────────────────────────────────────────────── */
  const [postType, setPostType] = useState("comment");
  const [text, setText]         = useState("");
  const [images, setImages]     = useState([]);     // uploaded URLs
  const [videoUrl, setVideoUrl] = useState("");
  const [linkUrl, setLinkUrl]   = useState("");
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [showLinkInput,  setShowLinkInput]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [posting,   setPosting]   = useState(false);
  const fileRef = useRef(null);

  const resetComposer = () => {
    setPostType("comment");
    setText("");
    setImages([]);
    setVideoUrl("");
    setLinkUrl("");
    setShowVideoInput(false);
    setShowLinkInput(false);
  };

  /* ─── data load ─────────────────────────────────────────────────── */
  const load = () => {
    setLoading(true);
    http.get(`/entity/${entityType}/${entityId}/messages?limit=100`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (entityType && entityId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId]);

  /* ─── image upload flow (per-file) ──────────────────────────────── */
  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const remaining = MAX_IMAGES - images.length;
    if (list.length > remaining) {
      toast.error(`You can attach at most ${MAX_IMAGES} images per post.`);
    }
    setUploading(true);
    const uploaded = [];
    for (const f of list.slice(0, remaining)) {
      if (f.size > MAX_BYTES) {
        toast.error(`"${f.name}" is too large (max 5 MB)`);
        continue;
      }
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(f);
      });
      try {
        const r = await http.post(`/entity/upload`, { image: dataUrl });
        if (r.data?.url) uploaded.push(r.data.url);
      } catch (e) {
        toast.error(`Upload failed for ${f.name}`);
      }
    }
    setImages((prev) => [...prev, ...uploaded].slice(0, MAX_IMAGES));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ─── sensitive-data guard ──────────────────────────────────────── */
  // Warn (soft) or block (hard) when a post looks like it contains
  // private info. Regexes are conservative to avoid false positives —
  // we err on the side of "warn once, let user confirm".
  const scanForSensitiveData = (raw) => {
    const s = String(raw || "");
    const hits = [];
    // Credit card — 13-19 digits with common Luhn-ish grouping.
    if (/\b(?:\d[ -]?){13,19}\b/.test(s)) {
      const nums = s.replace(/\D/g, "");
      if (nums.length >= 13 && nums.length <= 19) hits.push({ type: "credit card number", severity: "hard" });
    }
    // Passport / driver's-license — 8-9 chars mix of letters+digits with
    // an explicit "passport" or "license" nearby.
    if (/\b(passport|licen[cs]e|nric|aadhaar)[^\n]{0,30}\b[A-Z0-9]{6,12}\b/i.test(s))
      hits.push({ type: "passport / ID number", severity: "hard" });
    // PNR / booking reference — 6 alphanumerics adjacent to "PNR" or "booking".
    if (/\b(pnr|booking(?:\s*ref)?|confirmation)[^\n]{0,20}\b[A-Z0-9]{5,8}\b/i.test(s))
      hits.push({ type: "booking / PNR code", severity: "hard" });
    // Phone number — soft warn only (perfectly reasonable in a "call me back" tip).
    if (/(\+?\d[\d\s().-]{7,}\d)/.test(s)) hits.push({ type: "phone number", severity: "soft" });
    // Email — soft warn.
    if (/\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/i.test(s)) hits.push({ type: "email address", severity: "soft" });
    // Boarding pass looks like "Seat 12A · Gate B7 · Flight XX123".
    if (/\b(seat|gate|boarding)\s*[:#]?\s*\w{1,4}\b/i.test(s) && /\bflight\s*\w{1,7}\b/i.test(s))
      hits.push({ type: "boarding-pass details", severity: "soft" });
    return hits;
  };

  const passesSensitiveDataGuard = (body, title) => {
    const hits = scanForSensitiveData(`${title || ""}\n${body || ""}`);
    if (hits.length === 0) return true;
    const hard = hits.filter((h) => h.severity === "hard");
    const soft = hits.filter((h) => h.severity === "soft");
    const parts = [];
    if (hard.length) parts.push(`Blocked: your post appears to contain ${hard.map((h) => h.type).join(", ")}. Please remove that before posting.`);
    if (soft.length) parts.push(`Heads up — your post may contain ${soft.map((h) => h.type).join(", ")}. Post anyway?`);
    const msg = parts.join("\n\n");
    if (hard.length) {
      toast.error(msg);
      return false;
    }
    return window.confirm(msg);
  };

  /* ─── post ──────────────────────────────────────────────────────── */
  const acceptTermsIfNeeded = async () => {
    // Only prompt once — after first post we set localStorage.
    const stashKey = `wofy_terms_${entityType}`;
    if (localStorage.getItem(stashKey)) return true;
    const ok = window.confirm(
      "Community rules:\n\n" +
      "Please keep your post lawful and respectful. No harassment, hate speech, spam, or explicit content. " +
      "Reports are reviewed within 24 hours.\n\nTap OK to accept and post."
    );
    if (!ok) return false;
    try {
      await http.post(`/auth/accept-terms`, { version: "2026-02" });
      localStorage.setItem(stashKey, "1");
    } catch { /* server-side ack is best-effort */ localStorage.setItem(stashKey, "1"); }
    return true;
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!user) { toast.error("Sign in to leave a message"); return; }
    const body = text.trim();
    if (!body && !images.length && !videoUrl.trim() && !linkUrl.trim()) return;
    if (!passesSensitiveDataGuard(body)) return;
    if (!(await acceptTermsIfNeeded())) return;
    setPosting(true);
    try {
      const payload = {
        body:      body || " ",
        post_type: postType,
        images,
        video_url: videoUrl.trim() || null,
        link_url:  linkUrl.trim()  || null,
      };
      const r = await http.post(`/entity/${entityType}/${entityId}/messages`, payload);
      // Root posts land at the top with an empty replies array.
      setItems((prev) => [{ ...r.data, replies: [], reply_count: 0 }, ...prev]);
      resetComposer();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post your message. Please try again.");
    } finally {
      setPosting(false);
    }
  };

  const submitReply = async (rootId) => {
    if (!user) { toast.error("Sign in to reply"); return; }
    const body = (replyText || "").trim();
    if (!body) return;
    if (!passesSensitiveDataGuard(body)) return;
    if (!(await acceptTermsIfNeeded())) return;
    setReplyBusy(true);
    try {
      const r = await http.post(`/entity/${entityType}/${entityId}/messages`, {
        body,
        post_type: "comment",
        images: [],
        parent_id: rootId,
      });
      setItems((prev) => prev.map((m) => m.id === rootId
        ? { ...m, replies: [...(m.replies || []), r.data], reply_count: (m.reply_count || 0) + 1 }
        : m));
      setReplyText("");
      setReplyingTo(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post reply.");
    } finally {
      setReplyBusy(false);
    }
  };

  const reportPost = async (m) => {
    if (!user) { toast.error("Sign in to report content"); return; }
    const reason = window.prompt(
      "Report reason: abuse, spam, misinformation, harassment, hate, impersonation, other",
      "abuse");
    if (!reason) return;
    const notes = window.prompt("Anything else the moderator should know? (optional)", "") || "";
    try {
      await http.post(`/community/report`, {
        target_type: m.parent_id ? "reply" : "post",
        target_id:   m.id,
        reason:      reason.trim().toLowerCase(),
        notes:       notes.trim() || null,
      });
      toast.success("Reported — a moderator reviews within 24 hours.");
      setMenuFor(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit report.");
    }
  };

  const blockUser = async (m) => {
    if (!user) { toast.error("Sign in to block users"); return; }
    if (!window.confirm(`Block ${m.user_name}? You won't see any of their posts.`)) return;
    try {
      await http.post(`/community/block/${m.user_id}`);
      toast.success(`Blocked ${m.user_name}.`);
      setMenuFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not block user.");
    }
  };

  const remove = async (id) => {
    if (!id || !window.confirm("Delete this message?")) return;
    try {
      await http.delete(`/entity/messages/${id}`);
      setItems((prev) => prev.filter((m) => m.id !== id));
    } catch {
      toast.error("Could not delete this message");
    }
  };

  const react = async (id, emoji) => {
    if (!user) { toast.error("Sign in to react"); return; }
    // Optimistic update — flip the emoji locally and rollback on failure.
    setItems((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const mine = new Set(m.my_reactions || []);
      const counts = { ...(m.reaction_counts || {}) };
      if (mine.has(emoji)) {
        mine.delete(emoji);
        counts[emoji] = Math.max(0, (counts[emoji] || 1) - 1);
      } else {
        mine.add(emoji);
        counts[emoji] = (counts[emoji] || 0) + 1;
      }
      return { ...m, my_reactions: [...mine], reaction_counts: counts };
    }));
    try {
      const r = await http.post(`/entity/messages/${id}/react`, { emoji });
      setItems((prev) => prev.map((m) => m.id === id ? { ...m, reaction_counts: r.data.reaction_counts, my_reactions: r.data.my_reactions } : m));
    } catch (e) {
      toast.error("Could not save your reaction — try again.");
      // Refetch to sync back.
      load();
    }
  };

  const chip = POST_TYPES.find((p) => p.key === postType) || POST_TYPES[0];

  return (
    <section
      className="mt-8 rounded-3xl border p-5 sm:p-6"
      style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
      data-testid={`entity-board-${entityType}-${entityId}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
        <div className="font-display text-xl">{title}</div>
        <span className="ml-2 text-xs font-mono opacity-60">
          {items.length ? `${items.length} post${items.length === 1 ? "" : "s"}` : "be the first"}
        </span>
      </div>
      <p className="text-xs opacity-70">
        Share notes, corrections, or add local knowledge. Text · Images · Videos · Links.
      </p>

      {/* ─── Composer ─── */}
      <form onSubmit={submit} className="mt-4">
        {user ? (
          <div className="rounded-2xl border p-3 sm:p-4" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center shrink-0 overflow-hidden">
                {user.avatar_url
                  ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                  : <UserRound className="w-4 h-4 opacity-60" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <label className="text-[10px] font-mono uppercase tracking-widest opacity-60">Post type</label>
                  <select
                    value={postType}
                    onChange={(e) => setPostType(e.target.value)}
                    className="text-xs font-semibold px-2 py-1 rounded-full border focus:outline-none"
                    style={{ borderColor: chip.chip, color: chip.chip, background: "#fff" }}
                    data-testid="entity-board-post-type"
                  >
                    {COMPOSER_POST_TYPES.map((p) => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a post…"
                  rows={3}
                  maxLength={3000}
                  className="w-full text-sm rounded-xl border px-3 py-2 bg-transparent resize-none"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="entity-board-input"
                />
                {/* Attachments strip */}
                {(images.length > 0 || videoUrl || linkUrl) && (
                  <div className="mt-3 space-y-3">
                    {images.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="composer-images">
                        {images.map((u, i) => (
                          <div key={i} className="relative aspect-square rounded-lg overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                            <img src={toAbsoluteUrl(u)} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                              className="absolute top-1 right-1 rounded-full bg-black/70 text-white p-1"
                              aria-label="Remove image"
                              data-testid={`composer-image-remove-${i}`}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {showVideoInput && (
                      <input
                        type="url"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="YouTube or Vimeo URL"
                        className="w-full text-sm rounded-lg border px-3 py-2"
                        style={{ borderColor: "var(--wofy-border)" }}
                        data-testid="composer-video-input"
                      />
                    )}
                    {showLinkInput && (
                      <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="Paste a link URL"
                        className="w-full text-sm rounded-lg border px-3 py-2"
                        style={{ borderColor: "var(--wofy-border)" }}
                        data-testid="composer-link-input"
                      />
                    )}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/heic,image/gif"
                      multiple
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files)}
                      data-testid="composer-file-input"
                    />
                    <IconButton
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading || images.length >= MAX_IMAGES}
                      label={`Add photo (${images.length}/${MAX_IMAGES})`}
                      icon={ImageIcon}
                      tint="#0284C7"
                      testId="composer-photo-btn"
                    />
                    <IconButton
                      onClick={() => setShowVideoInput((v) => !v)}
                      label="Attach video"
                      icon={Video}
                      tint="#DC2626"
                      testId="composer-video-btn"
                    />
                    <IconButton
                      onClick={() => setShowLinkInput((v) => !v)}
                      label="Attach link"
                      icon={LinkIcon}
                      tint="#7C3AED"
                      testId="composer-link-btn"
                    />
                    <span className="ml-2 text-[11px] opacity-60">{text.length}/3000</span>
                    {uploading && <span className="text-[11px] opacity-70 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>}
                  </div>
                  <button
                    type="submit"
                    disabled={posting || (!text.trim() && !images.length && !videoUrl.trim() && !linkUrl.trim())}
                    className="wofy-btn-primary text-sm"
                    data-testid="entity-board-submit"
                  >
                    {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    {posting ? "Posting…" : "Post"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="rounded-2xl border p-4 flex items-center justify-between gap-3 flex-wrap"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          >
            <div className="text-sm">
              <div className="font-medium">Sign in to join the conversation</div>
              <div className="text-xs opacity-70 mt-0.5">
                Members-only board — keeps discussions high-quality.
              </div>
            </div>
            <Link to="/login" className="wofy-btn-primary text-sm" data-testid="entity-board-signin">
              <LogIn className="w-3.5 h-3.5" /> Sign in
            </Link>
          </div>
        )}
      </form>

      {/* ─── Filter tabs ─── */}
      <div className="mt-5 flex flex-wrap gap-1.5" data-testid="entity-board-tabs">
        {[{ key: "all", label: "All", chip: "#334155" }, ...COMPOSER_POST_TYPES].map((t) => {
          const on = tab === t.key;
          const n = t.key === "all"
            ? items.length
            : items.filter((m) => m.post_type === t.key).length;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition ${on ? "text-white" : ""}`}
              style={on
                ? { background: t.chip, borderColor: t.chip }
                : { borderColor: "var(--wofy-border)", color: t.chip, background: "transparent" }}
              data-testid={`entity-board-tab-${t.key}`}
            >
              {t.label} {n > 0 && <span className="opacity-70">({n})</span>}
            </button>
          );
        })}
      </div>

      {/* ─── Thread ─── */}
      <div className="mt-4 space-y-5" data-testid="entity-board-thread">
        {loading && <div className="text-sm opacity-60"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="text-sm opacity-60 text-center py-6">
            No posts yet. Kick things off — your community is listening.
          </div>
        )}
        {!loading && items
          .filter((m) => tab === "all" ? true : m.post_type === tab)
          .map((m) => (
            <MessageItem
              key={m.id}
              m={m}
              me={user}
              onDelete={() => remove(m.id)}
              onReact={(emoji) => react(m.id, emoji)}
              onReply={() => setReplyingTo(replyingTo === m.id ? null : m.id)}
              onReport={() => reportPost(m)}
              onBlock={() => blockUser(m)}
              menuOpen={menuFor === m.id}
              onMenuToggle={() => setMenuFor(menuFor === m.id ? null : m.id)}
              canReact={!!user}
              replyingTo={replyingTo}
              replyText={replyText}
              setReplyText={setReplyText}
              submitReply={submitReply}
              replyBusy={replyBusy}
              react={react}
              reportPost={reportPost}
              blockUser={blockUser}
              menuFor={menuFor}
              setMenuFor={setMenuFor}
            />
          ))}
      </div>
    </section>
  );
}

/* ─── message renderer ─────────────────────────────────────────────── */
function MessageItem({
  m, me, onDelete, onReact, onReply, onReport, onBlock,
  menuOpen, onMenuToggle,
  canReact, replyingTo, replyText, setReplyText, submitReply, replyBusy,
  react, reportPost, blockUser, menuFor, setMenuFor,
}) {
  const chip = POST_TYPES.find((p) => p.key === m.post_type) || POST_TYPES[0];
  const embed = parseVideoEmbed(m.video_url);
  const mine   = new Set(m.my_reactions || []);
  const counts = m.reaction_counts || {};
  // Verified business reply styling — the backend already forbids this
  // post_type unless the author is a verified owner of the entity.
  const isBusinessReply = m.post_type === "business_reply";
  const content = (
    <article
      className={`flex items-start gap-3 ${isBusinessReply ? "rounded-xl border p-3" : ""}`}
      style={isBusinessReply ? { borderColor: "#166534", background: "linear-gradient(180deg,#F0FDF4,#DCFCE7)" } : undefined}
      data-testid={`entity-board-msg-${m.id}`}
    >
      <div className="w-9 h-9 rounded-full bg-black/5 flex items-center justify-center shrink-0 overflow-hidden">
        {m.user_avatar
          ? <img src={m.user_avatar} alt="" className="w-full h-full object-cover" />
          : <UserRound className="w-4 h-4 opacity-60" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="font-medium truncate">{m.user_name}</span>
          {isBusinessReply && (
            <span
              className="text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 inline-flex items-center gap-1"
              style={{ background: "#166534", color: "#F0FDF4" }}
              title="Response from the verified owner of this business"
              data-testid={`business-reply-badge-${m.id}`}
            >
              ✓ Verified Business Response
            </span>
          )}
          <span className="opacity-50">· {relTime(m.created_at)}</span>
          {m.is_mine && (
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">you</span>
          )}
          {!isBusinessReply && m.post_type && m.post_type !== "comment" && (
            <span
              className="text-[10px] font-mono uppercase tracking-widest rounded-full px-1.5 py-0.5"
              style={{ background: `${chip.chip}18`, color: chip.chip }}
              data-testid={`entity-board-msg-badge-${m.id}`}
            >
              {chip.label}
            </span>
          )}
        </div>
        {m.title && (
          <div className="mt-1 text-sm font-bold" style={{ color: isBusinessReply ? "#166534" : "#0F3D2E" }} data-testid={`msg-title-${m.id}`}>
            {m.title}
          </div>
        )}
        <div className={`mt-1 text-sm leading-relaxed whitespace-pre-wrap break-words ${isBusinessReply ? "font-medium" : ""}`}>
          {m.body}
        </div>
        {Array.isArray(m.images) && m.images.length > 0 && (
          <div className={`mt-2 grid gap-1 ${m.images.length === 1 ? "" : "grid-cols-3"}`} data-testid={`msg-images-${m.id}`}>
            {m.images.slice(0, 6).map((u, i) => (
              <a
                key={i}
                href={toAbsoluteUrl(u)}
                target="_blank"
                rel="noreferrer"
                className={`overflow-hidden rounded-lg border ${m.images.length === 1 ? "" : "aspect-square"}`}
                style={{ borderColor: "var(--wofy-border)" }}
              >
                <img
                  src={toAbsoluteUrl(u)}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover hover:opacity-90 transition"
                />
              </a>
            ))}
          </div>
        )}
        {embed && (
          <div className="mt-2 rounded-lg overflow-hidden border aspect-video" style={{ borderColor: "var(--wofy-border)" }} data-testid={`msg-video-${m.id}`}>
            <iframe
              src={embed.src}
              title={`${embed.kind} embed`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        )}
        {m.link_url && !embed && (
          <a
            href={m.link_url}
            target="_blank"
            rel="noreferrer"
            className="mt-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs hover:bg-black/[0.03] transition"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid={`msg-link-${m.id}`}
          >
            <LinkIcon className="w-3.5 h-3.5 opacity-60" />
            <span className="truncate">{m.link_url}</span>
          </a>
        )}
        {/* Reaction bar — 6 emojis, hover-brightens, pressed state
            when the current user's already reacted. Counts hidden
            when 0 to keep the row tidy. */}
        <div className="mt-2 flex items-center gap-1 flex-wrap" data-testid={`msg-reactions-${m.id}`}>
          {REACTIONS.map((r) => {
            const active = mine.has(r.key);
            const count  = counts[r.key] || 0;
            return (
              <button
                key={r.key}
                type="button"
                onClick={() => onReact?.(r.key)}
                disabled={!canReact}
                title={r.label}
                aria-label={`${r.label} (${count})`}
                aria-pressed={active}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs transition ${active ? "shadow-inner" : "hover:bg-black/[0.04]"} disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{
                  borderColor: active ? "var(--wofy-primary)" : "var(--wofy-border)",
                  background:  active ? "rgba(245,130,32,0.10)" : "transparent",
                }}
                data-testid={`msg-react-${m.id}-${r.key}`}
              >
                <span className="text-sm leading-none">{r.emoji}</span>
                {count > 0 && <span className="font-mono opacity-70">{count}</span>}
              </button>
            );
          })}
        </div>
      </div>
      {/* Actions row + kebab */}
      <div className="ml-2 flex items-start gap-1 shrink-0 relative">
        {!m.parent_id && (
          <button
            onClick={onReply}
            title="Reply"
            className="p-1.5 rounded-full hover:bg-black/[0.05] text-xs opacity-70 hover:opacity-100"
            data-testid={`entity-board-reply-${m.id}`}
          >
            <MessageCircle className="w-3.5 h-3.5"/>
          </button>
        )}
        {m.is_mine && (
          <button
            onClick={onDelete}
            title="Delete"
            className="p-1.5 rounded-full text-red-500 hover:bg-red-50"
            data-testid={`entity-board-delete-${m.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
        {!m.is_mine && me && (
          <button
            onClick={onMenuToggle}
            title="More"
            className="p-1.5 rounded-full hover:bg-black/[0.05] text-xs opacity-70 hover:opacity-100"
            data-testid={`entity-board-menu-${m.id}`}
          >
            ⋯
          </button>
        )}
        {menuOpen && (
          <div className="absolute right-0 top-8 z-10 rounded-lg border bg-white shadow-lg text-xs w-40"
               style={{ borderColor: "var(--wofy-border)" }}
               data-testid={`entity-board-menu-panel-${m.id}`}>
            <button onClick={onReport}
                    className="w-full text-left px-3 py-2 hover:bg-black/[0.03]"
                    data-testid={`entity-board-report-${m.id}`}>
              🚩 Report
            </button>
            <button onClick={onBlock}
                    className="w-full text-left px-3 py-2 hover:bg-black/[0.03] border-t"
                    style={{ borderColor: "var(--wofy-border)" }}
                    data-testid={`entity-board-block-${m.id}`}>
              🚫 Block user
            </button>
          </div>
        )}
      </div>
    </article>
    );

  // Wrap the whole card + inline reply form + replies in a fragment.
  const rootReplies = m.replies || [];
  return (
    <>
      {content}
      {replyingTo === m.id && (
        <form
          onSubmit={(e) => { e.preventDefault(); submitReply(m.id); }}
          className="ml-12 mt-2 flex items-center gap-2"
          data-testid={`entity-board-reply-form-${m.id}`}
        >
          <input
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={`Reply to ${m.user_name}…`}
            className="flex-1 text-sm rounded-full border px-3 py-1.5"
            style={{ borderColor: "var(--wofy-border)" }}
            autoFocus
            maxLength={1500}
            data-testid={`entity-board-reply-input-${m.id}`}
          />
          <button type="submit"
                  disabled={replyBusy || !replyText.trim()}
                  className="px-3 py-1.5 rounded-full text-white text-xs font-semibold disabled:opacity-40"
                  style={{ background: "var(--wofy-primary)" }}
                  data-testid={`entity-board-reply-submit-${m.id}`}>
            {replyBusy ? "…" : "Reply"}
          </button>
        </form>
      )}
      {rootReplies.length > 0 && (
        <div className="ml-12 mt-3 space-y-3 border-l pl-4"
             style={{ borderColor: "var(--wofy-border)" }}
             data-testid={`entity-board-replies-${m.id}`}>
          {rootReplies.map((r) => (
            <ReplyItem
              key={r.id}
              r={r}
              me={me}
              react={react}
              reportPost={reportPost}
              blockUser={blockUser}
              menuFor={menuFor}
              setMenuFor={setMenuFor}
            />
          ))}
        </div>
      )}
    </>
  );
}

/* Single reply — no further nesting; kept separate so babel doesn't
   trip on MessageItem referencing itself. */
function ReplyItem({ r, me, react, reportPost, blockUser, menuFor, setMenuFor }) {
  const chip = POST_TYPES.find((p) => p.key === r.post_type) || POST_TYPES[0];
  const menuOpen = menuFor === r.id;
  const remove = () => {
    if (!window.confirm("Delete this reply?")) return;
    http.delete(`/entity/messages/${r.id}`).catch(() => toast.error("Could not delete"));
  };
  return (
    <article className="flex items-start gap-2" data-testid={`entity-board-reply-${r.id}`}>
      <div className="w-7 h-7 rounded-full bg-black/5 flex items-center justify-center shrink-0 overflow-hidden">
        {r.user_avatar
          ? <img src={r.user_avatar} alt="" className="w-full h-full object-cover"/>
          : <UserRound className="w-3.5 h-3.5 opacity-60"/>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="font-medium truncate">{r.user_name}</span>
          <span className="opacity-50">· {relTime(r.created_at)}</span>
          {r.is_mine && <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">you</span>}
          {r.post_type && r.post_type !== "comment" && (
            <span className="text-[10px] font-mono uppercase tracking-widest rounded-full px-1.5 py-0.5"
                  style={{ background: `${chip.chip}18`, color: chip.chip }}>
              {chip.label}
            </span>
          )}
        </div>
        <div className="mt-0.5 text-sm leading-relaxed whitespace-pre-wrap break-words">{r.body}</div>
      </div>
      <div className="relative shrink-0">
        {r.is_mine ? (
          <button onClick={remove} title="Delete"
                  className="p-1 rounded-full text-red-500 hover:bg-red-50"
                  data-testid={`entity-board-reply-delete-${r.id}`}>
            <Trash2 className="w-3 h-3"/>
          </button>
        ) : me && (
          <button onClick={() => setMenuFor(menuOpen ? null : r.id)} title="More"
                  className="p-1 rounded-full hover:bg-black/[0.05] text-xs opacity-70"
                  data-testid={`entity-board-reply-menu-${r.id}`}>⋯</button>
        )}
        {menuOpen && (
          <div className="absolute right-0 top-7 z-10 rounded-lg border bg-white shadow-lg text-xs w-40"
               style={{ borderColor: "var(--wofy-border)" }}>
            <button onClick={() => reportPost(r)}
                    className="w-full text-left px-3 py-2 hover:bg-black/[0.03]">🚩 Report</button>
            <button onClick={() => blockUser(r)}
                    className="w-full text-left px-3 py-2 hover:bg-black/[0.03] border-t"
                    style={{ borderColor: "var(--wofy-border)" }}>🚫 Block user</button>
          </div>
        )}
      </div>
    </article>
  );
}

/* ─── icon-only composer button ────────────────────────────────────── */
function IconButton({ onClick, disabled, label, icon: Icon, tint, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="p-2 rounded-full hover:bg-black/[0.05] disabled:opacity-40 transition"
      data-testid={testId}
    >
      <Icon className="w-4 h-4" style={{ color: tint }} />
    </button>
  );
}
